import {
  Box,
  Button,
  Checkbox,
  CreatableMultiSelect,
  Flex,
  Spinner,
} from '@ouster/webviz-react-components';

import { Tooltip } from '@mui/material';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import useChakraHook from '../../hooks/useChakraHook';
import { useStoreActions, useStoreState } from '../../reducers';
import { MappedOrg } from '../../types';
import { OUSTER_ORG_ID } from '../../utils/constants';

type CopyRecordingToOrganizationsProps = {
  driveId: number | string;
  organizationId: number;
  recordingTitle?: string;
  onClose: () => void;
};

type CopyModalOverflowTooltipProps = {
  value: string;
  dataTestId: string;
};

type CopyResult = {
  already_existed: boolean;
  drive_id: number;
  organization_id: number;
  organization_name: string;
  recording_title: string;
};

type CopyRecordingResponse = {
  copies: CopyResult[];
  success: boolean;
};

function CopyModalOverflowTooltip({
  value,
  dataTestId,
}: CopyModalOverflowTooltipProps) {
  const textRef = useRef<HTMLDivElement | null>(null);
  const [isOverflow, setIsOverflow] = useState(false);

  const checkOverflow = useCallback(() => {
    const element = textRef.current;
    if (!element) return;

    setIsOverflow(
      element.scrollHeight > element.clientHeight ||
        element.scrollWidth > element.clientWidth
    );
  }, []);

  useEffect(() => {
    checkOverflow();

    if (typeof ResizeObserver === 'undefined') {
      return undefined;
    }

    const resizeObserver = new ResizeObserver(checkOverflow);
    if (textRef.current) {
      resizeObserver.observe(textRef.current);
    }

    return () => resizeObserver.disconnect();
  }, [checkOverflow, value]);

  return (
    <Tooltip
      title={
        <span
          style={{
            fontSize: 14,
            lineHeight: '20px',
          }}
        >
          {value}
        </span>
      }
      arrow
      placement="top-start"
      disableHoverListener={!isOverflow}
      disableFocusListener={!isOverflow}
      disableTouchListener={!isOverflow}
      PopperProps={{
        sx: { zIndex: 20003 },
      }}
    >
      <span style={{ display: 'block', width: '100%', minWidth: 0 }}>
        <div
          ref={textRef}
          style={{
            display: '-webkit-box',
            width: '100%',
            minWidth: 0,
            fontSize: '14px',
            overflow: 'hidden',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            lineHeight: '20px',
            maxHeight: '40px',
            wordBreak: 'break-word',
          }}
          data-testid={dataTestId}
        >
          {value}
        </div>
      </span>
    </Tooltip>
  );
}

export default function CopyRecordingToOrganizations({
  driveId,
  organizationId,
  recordingTitle,
  onClose,
}: CopyRecordingToOrganizationsProps) {
  const { fetchFullOrgList } = useStoreActions(
    (actions) => actions.organization
  );
  const { copyRecordingToOrganizations } = useStoreActions(
    (actions) => actions.drive
  );
  const { fullOrgList } = useStoreState((state) => state.organization);
  const { showToast } = useChakraHook();

  const [selectedOrgs, setSelectedOrgs] = useState<MappedOrg[]>([]);
  const [isLoadingOptions, setIsLoadingOptions] = useState(
    fullOrgList.length === 0
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [sendNotification, setSendNotification] = useState(true);

  useEffect(() => {
    let isMounted = true;

    if (fullOrgList.length > 0) {
      // isLoadingOptions can't be derived purely from fullOrgList.length:
      // once the async fetchFullOrgList() below resolves (see the finally
      // block), isLoadingOptions must become false even if the fetch
      // returned an empty list (fullOrgList.length still 0). This branch
      // just short-circuits that same synchronization when the org list
      // is already populated, so no fetch is needed.
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setIsLoadingOptions(false);
      return () => {
        isMounted = false;
      };
    }

    setIsLoadingOptions(true);
    const loadOrganizations = async () => {
      try {
        await fetchFullOrgList();
      } finally {
        if (isMounted) {
          setIsLoadingOptions(false);
        }
      }
    };

    loadOrganizations();

    return () => {
      isMounted = false;
    };
  }, [fetchFullOrgList, fullOrgList.length]);

  const mappedOrgs = useMemo<MappedOrg[]>(
    () =>
      fullOrgList
        .filter(
          (org) => org.org_id !== organizationId && org.org_id !== OUSTER_ORG_ID
        )
        .map((org) => ({
          label: `${org.org_name} (${org.creator_email})`,
          value: org.org_id.toString(),
          data: org,
        })),
    [fullOrgList, organizationId]
  );

  const handleSubmit = async () => {
    if (selectedOrgs.length === 0) return;

    setIsSubmitting(true);
    try {
      const response = (await copyRecordingToOrganizations({
        organizationId,
        driveId,
        destinationOrganizationIds: selectedOrgs.map((org) => org.data.org_id),
        sendNotification,
      })) as CopyRecordingResponse;

      const copiedEntries = response.copies.filter(
        (copy) => !copy.already_existed
      );
      const existingEntries = response.copies.filter(
        (copy) => copy.already_existed
      );
      const resolvedRecordingTitle =
        response.copies[0]?.recording_title ?? recordingTitle ?? 'Recording';
      const descriptionParts: string[] = [];

      if (copiedEntries.length > 0) {
        descriptionParts.push(
          `Recording ${resolvedRecordingTitle} copied to Org(s): ${copiedEntries
            .map((copy) => copy.organization_name)
            .join(', ')}.`
        );
      }

      if (existingEntries.length > 0) {
        descriptionParts.push(
          `Recording ${resolvedRecordingTitle} already exists in Org(s): ${existingEntries
            .map((copy) => copy.organization_name)
            .join(', ')}.`
        );
      }

      showToast({
        title: 'Recording copied',
        description:
          descriptionParts.join('\n') ||
          `No copy results were returned for recording ${resolvedRecordingTitle}.`,
        status: 'success',
      });
      onClose();
    } catch (error) {
      showToast({
        title: 'Failed to copy recording',
        description: `${error}`,
        status: 'error',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box
      data-testid="copy-recording-to-organizations-root"
      width="min(42rem, calc(100vw - 6rem))"
      maxWidth="100%"
      minWidth={0}
      overflow="hidden"
    >
      <Box mb={4} display="grid" rowGap="0.75rem" width="100%" minWidth={0}>
        <Box color="whiteAlpha.900" fontSize="14px" lineHeight="20px">
          Copy this recording to one or more organizations.
        </Box>
        {recordingTitle && (
          <Box
            display="flex"
            alignItems="flex-start"
            columnGap="0.5rem"
            width="100%"
            minWidth={0}
            fontSize="14px"
            lineHeight="20px"
          >
            <Box as="span" flexShrink={0} fontWeight={500}>
              Recording:
            </Box>
            <Box display="block" flex={1} minWidth={0}>
              <CopyModalOverflowTooltip
                value={recordingTitle}
                dataTestId="copy-recording-to-organizations-title-tooltip"
              />
            </Box>
          </Box>
        )}
      </Box>

      {isLoadingOptions ? (
        <Flex justifyContent="center" py={6}>
          <Spinner data-testid="copy-recording-to-organizations-loading" />
        </Flex>
      ) : isSubmitting ? (
        <Box display="flex" justifyContent="left" columnGap={8} py={2}>
          <Spinner data-testid="copy-recording-to-organizations-submitting" />
          Action in progress
        </Box>
      ) : (
        <>
          <Box
            as="span"
            fontSize={13}
            color="white"
            textTransform="uppercase"
            fontWeight="medium"
          >
            {selectedOrgs.length} destination organizations selected
          </Box>
          <Box mt={3} width="100%" maxWidth="100%" minWidth={0}>
            <CreatableMultiSelect
              placeholderText="Select destination organizations..."
              menuPlacement="top"
              autoWrap
              width="100%"
              maxWidth="100%"
              minWidth={0}
              options={mappedOrgs}
              selectedOptions={selectedOrgs}
              onOptionChange={(items) => setSelectedOrgs(items as MappedOrg[])}
              chakraStyles={{
                control: (provided, state: { hasValue: boolean }) => ({
                  ...provided,
                  width: '100%',
                  maxWidth: '100%',
                  minWidth: 0,
                  maxHeight: state.hasValue
                    ? 'calc(var(--ouster-sizes-10) * 6)'
                    : provided.maxHeight,
                  flexWrap: 'nowrap',
                  alignItems: state.hasValue
                    ? 'flex-start'
                    : provided.alignItems,
                  overflowY: state.hasValue ? 'auto' : provided.overflowY,
                  '&::-webkit-scrollbar': {
                    width: '6px',
                  },
                  '&::-webkit-scrollbar-track': {
                    background: 'transparent',
                  },
                  '&::-webkit-scrollbar-thumb': {
                    background: 'gray.500',
                    borderRadius: '4px',
                    cursor: 'pointer',
                  },
                  '&::-webkit-scrollbar-thumb:hover': {
                    background: 'gray.300',
                  },
                }),
                valueContainer: (provided, state: { hasValue: boolean }) => ({
                  ...provided,
                  display: 'flex',
                  flex: '1 1 auto',
                  alignItems: state.hasValue
                    ? 'flex-start'
                    : provided.alignItems,
                  width: '100%',
                  maxWidth: '100%',
                  minWidth: 0,
                  flexWrap: 'wrap',
                  overflowX: 'visible',
                  overflowY: 'visible',
                }),
                multiValue: (provided) => ({
                  ...provided,
                  minWidth: 0,
                  flexShrink: 1,
                  maxWidth: '100%',
                }),
                multiValueLabel: (provided) => ({
                  ...provided,
                  maxWidth: '100%',
                  whiteSpace: 'normal',
                  overflowWrap: 'anywhere',
                }),
                indicatorsContainer: (
                  provided,
                  state: { hasValue: boolean }
                ) => ({
                  ...provided,
                  flex: '0 0 auto',
                  flexShrink: 0,
                  alignSelf: state.hasValue ? 'flex-start' : provided.alignSelf,
                  position: 'relative',
                  zIndex: 1,
                }),
                inputContainer: (provided, state: { hasValue: boolean }) => ({
                  ...provided,
                  minWidth: '6ch',
                  flex: '1 0 6ch',
                  alignSelf: state.hasValue ? 'stretch' : provided.alignSelf,
                }),
                input: (provided) => ({
                  ...provided,
                  minWidth: 0,
                  width: '100%',
                }),
              }}
              formatOptionLabel={(option, { context }) => {
                const mappedOption = option as MappedOrg;
                if (context === 'value') {
                  return mappedOption.label;
                }

                return mappedOption.label;
              }}
              data-testid="copy-recording-to-organizations-multiselect"
            />
          </Box>
          <Box mt={4}>
            <Checkbox
              label="Send email notification to target organization(s) admins"
              isChecked={sendNotification}
              onChange={(e) => setSendNotification(e.target.checked)}
              data-testid="copy-recording-send-notification-checkbox"
            />
          </Box>
          {mappedOrgs.length === 0 && (
            <Box mt={3} color="whiteAlpha.700">
              No destination organizations are currently available.
            </Box>
          )}
        </>
      )}

      <Flex justifyContent="flex-end" gap={3} mt={6}>
        <Button
          variant="ghost"
          onClick={onClose}
          disabled={isSubmitting}
          data-testid="copy-recording-to-organizations-cancel"
        >
          Cancel
        </Button>
        <Button
          variant="solid"
          onClick={handleSubmit}
          isLoading={isSubmitting}
          disabled={
            isSubmitting || isLoadingOptions || selectedOrgs.length === 0
          }
          data-testid="copy-recording-to-organizations-submit"
        >
          Copy to organization(s)
        </Button>
      </Flex>
    </Box>
  );
}
